import { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import OfferDeskNav from '../components/OfferDeskNav';

/* ─── Config ─────────────────────────────────────────────── */
const FEEDBACK_TYPES = ['All', 'Bug', 'Suggestion', 'Positive', 'Other', 'General'];

const TYPE_CONFIG = {
  Bug:        { bg: '#FFF0F0', color: '#991B1B', border: '#FECACA', dot: '#EF4444' },
  Suggestion: { bg: '#EEF2FF', color: '#4338CA', border: '#C7D2FE', dot: '#4338CA' },
  Positive:   { bg: '#ECFDF5', color: '#065F46', border: '#A7F3D0', dot: '#10B981' },
  Other:      { bg: 'var(--tandm-canvas-deep)', color: 'var(--tandm-body)', border: 'var(--tandm-line)', dot: 'var(--tandm-body)' },
  General:    { bg: 'var(--tandm-canvas-deep)', color: 'var(--tandm-body)', border: 'var(--tandm-line)', dot: 'var(--tandm-body)' },
};

/* ─── Icons ──────────────────────────────────────────────── */
const Ic = {
  internship: () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 002-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>,
  users:      () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
  message:    () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>,
  sparkle:    () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>,
  zap:        () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  clock:      () => <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2"/></svg>,
  check:      () => <svg width="14" height="14" fill="none" stroke="#fff" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>,
  refresh:    () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>,
  link:       () => <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path strokeLinecap="round" strokeLinejoin="round" d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>,
  external:   () => <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  trash:      () => <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><polyline points="3 6 5 6 21 6"/><path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>,
};

/* ─── FadeIn ─────────────────────────────────────────────── */
function FadeIn({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

/* ─── Card ───────────────────────────────────────────────── */
function Card({ children, style }) {
  return (
    <div style={{
      background: 'var(--tandm-surface)', border: '1px solid var(--tandm-line)',
      borderRadius: 20, padding: '22px', boxShadow: 'var(--tandm-shadow-soft)', ...style,
    }}>
      {children}
    </div>
  );
}

/* ─── Users Tab ──────────────────────────────────────────── */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/feedback/users');
      setUsers(res.data.users || []);
    } catch {}
    finally { setLoading(false); }
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return !q ||
      u.email?.toLowerCase().includes(q) ||
      u.first_name?.toLowerCase().includes(q) ||
      u.last_name?.toLowerCase().includes(q) ||
      u.university?.toLowerCase().includes(q);
  });

  const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const withCV = users.filter(u => u.cv_url).length;
  const withSectors = users.filter(u => u.target_sectors?.length > 0).length;
  const international = users.filter(u => u.is_international).length;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 22 }}>
        {[
          { label: 'Total Users',   value: users.length,  color: 'var(--tandm-accent)' },
          { label: 'CV Uploaded',   value: withCV,        color: '#6366F1' },
          { label: 'Sectors Set',   value: withSectors,   color: '#10B981' },
          { label: 'International', value: international, color: '#F59E0B' },
        ].map((s, i) => (
          <FadeIn key={s.label} delay={i * 0.06}>
            <div style={{ background: 'var(--tandm-surface)', backdropFilter: 'blur(16px)', border: '1px solid var(--tandm-line)', borderRadius: 16, padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tandm-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{s.label}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: s.color, letterSpacing: '-0.04em', lineHeight: 1 }}>{s.value}</div>
            </div>
          </FadeIn>
        ))}
      </div>

      <FadeIn delay={0.1}>
        <div style={{ position: 'relative', marginBottom: 18 }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--tandm-muted)' }}>
            <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          </span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email or university..."
            style={{ width: '100%', padding: '11px 14px 11px 40px', border: '1.5px solid var(--tandm-line)', borderRadius: 12, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: 'var(--tandm-ink)', background: 'var(--tandm-surface)', outline: 'none' }} />
        </div>
      </FadeIn>

      <p style={{ fontSize: 12, color: 'var(--tandm-muted)', margin: '0 0 14px' }}>{loading ? 'Loading...' : `${filtered.length} user${filtered.length !== 1 ? 's' : ''}`}</p>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <div style={{ width: 32, height: 32, border: '3px solid var(--tandm-canvas-deep)', borderTopColor: 'var(--tandm-accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((u, i) => (
            <motion.div key={u.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} whileHover={{ y: -2, boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }}
              style={{ background: 'var(--tandm-surface)', backdropFilter: 'blur(16px)', border: '1px solid var(--tandm-line)', borderRadius: 16, padding: '16px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, background: 'var(--tandm-gradient-cta)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: '#fff' }}>
                  {u.first_name?.charAt(0)?.toUpperCase() || u.email?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-ink)' }}>
                      {u.first_name || ''} {u.last_name || ''}
                      {!u.first_name && !u.last_name && <span style={{ color: 'var(--tandm-muted)', fontWeight: 400 }}>No name set</span>}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--tandm-body)' }}>{u.email}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {u.university && <span style={{ fontSize: 11, background: '#EEF2FF', color: '#4338CA', padding: '2px 9px', borderRadius: 100, fontWeight: 500 }}>{u.university}</span>}
                    {u.degree && <span style={{ fontSize: 11, background: 'var(--tandm-canvas-deep)', color: 'var(--tandm-body)', padding: '2px 9px', borderRadius: 100 }}>{u.degree}</span>}
                    {u.graduation_year && <span style={{ fontSize: 11, color: 'var(--tandm-muted)' }}>Class of {u.graduation_year}</span>}
                    {u.is_international && <span style={{ fontSize: 11, background: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A', padding: '2px 9px', borderRadius: 100, fontWeight: 600 }}>International</span>}
                    {u.cv_url && <span style={{ fontSize: 11, background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0', padding: '2px 9px', borderRadius: 100, fontWeight: 600 }}>CV uploaded</span>}
                    {u.target_sectors?.length > 0 && <span style={{ fontSize: 11, background: '#FFF7ED', color: 'var(--tandm-accent)', padding: '2px 9px', borderRadius: 100 }}>{u.target_sectors.length} sector{u.target_sectors.length > 1 ? 's' : ''}</span>}
                    {parseInt(u.application_count) > 0 && <span style={{ fontSize: 11, color: 'var(--tandm-muted)' }}>{u.application_count} application{u.application_count > 1 ? 's' : ''}</span>}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--tandm-faint)', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                  <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2"/></svg>
                  {formatDate(u.created_at)}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Waitlist Tab ───────────────────────────────────────── */
function WaitlistTab() {
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchWaitlist(); }, []);

  const fetchWaitlist = async () => {
    setLoading(true);
    try {
      const res = await api.get('/waitlist');
      setWaitlist(res.data.waitlist || []);
    } catch {}
    finally { setLoading(false); }
  };

  const filtered = waitlist.filter(w => {
    const q = search.toLowerCase();
    return !q ||
      w.name?.toLowerCase().includes(q) ||
      w.email?.toLowerCase().includes(q) ||
      w.university?.toLowerCase().includes(q) ||
      w.course?.toLowerCase().includes(q);
  });

  const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const universities = [...new Set(waitlist.map(w => w.university).filter(Boolean))];

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 22 }}>
        {[
          { label: 'Total Signups',   value: waitlist.length,      color: 'var(--tandm-accent)' },
          { label: 'Universities',    value: universities.length,   color: '#6366F1' },
          { label: 'With WhatsApp',   value: waitlist.filter(w => w.whatsapp).length, color: '#10B981' },
          { label: 'Today',           value: waitlist.filter(w => new Date(w.created_at).toDateString() === new Date().toDateString()).length, color: '#F59E0B' },
        ].map((s, i) => (
          <FadeIn key={s.label} delay={i * 0.06}>
            <div style={{ background: 'var(--tandm-surface)', backdropFilter: 'blur(16px)', border: '1px solid var(--tandm-line)', borderRadius: 16, padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tandm-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{s.label}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: s.color, letterSpacing: '-0.04em', lineHeight: 1 }}>{s.value}</div>
            </div>
          </FadeIn>
        ))}
      </div>

      {/* Search + Refresh */}
      <FadeIn delay={0.1}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--tandm-muted)' }}>
              <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, university or course..."
              style={{ width: '100%', padding: '11px 14px 11px 40px', border: '1.5px solid var(--tandm-line)', borderRadius: 12, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: 'var(--tandm-ink)', background: 'var(--tandm-surface)', outline: 'none' }} />
          </div>
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={fetchWaitlist}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--tandm-body)', background: 'var(--tandm-surface)', border: '1.5px solid var(--tandm-line)', borderRadius: 12, padding: '0 16px', cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)', whiteSpace: 'nowrap' }}>
            <Ic.refresh /> Refresh
          </motion.button>
        </div>
      </FadeIn>

      <p style={{ fontSize: 12, color: 'var(--tandm-muted)', margin: '0 0 14px' }}>
        {loading ? 'Loading...' : `${filtered.length} signup${filtered.length !== 1 ? 's' : ''}`}
      </p>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <div style={{ width: 32, height: 32, border: '3px solid var(--tandm-canvas-deep)', borderTopColor: 'var(--tandm-accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 24px', background: 'var(--tandm-surface)', backdropFilter: 'blur(16px)', border: '1px solid var(--tandm-line)', borderRadius: 20 }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--tandm-ink)', marginBottom: 6 }}>No signups yet</p>
          <p style={{ fontSize: 13, color: 'var(--tandm-muted)' }}>Waitlist signups will appear here</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((w, i) => (
            <motion.div key={w.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              whileHover={{ y: -2, boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }}
              style={{ background: 'var(--tandm-surface)', backdropFilter: 'blur(16px)', border: '1px solid var(--tandm-line)', borderRadius: 16, padding: '16px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                {/* Avatar */}
                <div style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, background: 'var(--tandm-gradient-cta)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: '#fff' }}>
                  {w.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-ink)' }}>{w.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--tandm-body)' }}>{w.email}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {w.university && <span style={{ fontSize: 11, background: '#EEF2FF', color: '#4338CA', padding: '2px 9px', borderRadius: 100, fontWeight: 500 }}>{w.university}</span>}
                    {w.course && <span style={{ fontSize: 11, background: 'var(--tandm-canvas-deep)', color: 'var(--tandm-body)', padding: '2px 9px', borderRadius: 100 }}>{w.course}</span>}
                    {w.whatsapp && <span style={{ fontSize: 11, background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0', padding: '2px 9px', borderRadius: 100 }}>{w.whatsapp}</span>}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--tandm-faint)', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                  <Ic.clock /> {formatDate(w.created_at)}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Feedback Tab ───────────────────────────────────────── */
function FeedbackTab() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('All');
  const [pageFilter, setPageFilter] = useState('All');

  useEffect(() => { fetchFeedback(); }, []);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const res = await api.get('/feedback');
      setFeedback(res.data.feedback || []);
    } catch {}
    finally { setLoading(false); }
  };

  const pages = ['All', ...new Set(feedback.map(f => f.page).filter(Boolean))];

  const filtered = feedback.filter(f => {
    const typeMatch = typeFilter === 'All' || f.type === typeFilter;
    const pageMatch = pageFilter === 'All' || f.page === pageFilter;
    return typeMatch && pageMatch;
  });

  const counts = FEEDBACK_TYPES.reduce((acc, t) => {
    acc[t] = t === 'All' ? feedback.length : feedback.filter(f => f.type === t).length;
    return acc;
  }, {});

  const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' · ' +
      date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px,1fr))', gap: 10, marginBottom: 22 }}>
        {['Bug', 'Suggestion', 'Positive', 'Other'].map((t, i) => {
          const cfg = TYPE_CONFIG[t];
          return (
            <FadeIn key={t} delay={i * 0.06}>
              <motion.div
                whileHover={{ y: -3 }}
                onClick={() => setTypeFilter(typeFilter === t ? 'All' : t)}
                style={{
                  background: typeFilter === t ? cfg.bg : 'var(--tandm-surface)',
                  backdropFilter: 'blur(16px)', border: `1.5px solid ${typeFilter === t ? cfg.border : 'var(--tandm-line)'}`,
                  borderRadius: 16, padding: '14px 16px', cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot }} />
                  <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--tandm-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t}</span>
                </div>
                <p style={{ fontSize: 24, fontWeight: 800, color: counts[t] > 0 ? cfg.color : 'var(--tandm-faint)', letterSpacing: '-0.04em', margin: 0 }}>{counts[t]}</p>
              </motion.div>
            </FadeIn>
          );
        })}
      </div>

      {/* Filters */}
      <FadeIn delay={0.1}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {/* Type filter */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FEEDBACK_TYPES.filter(t => t !== 'General').map(t => {
              const active = typeFilter === t;
              const cfg = t !== 'All' ? TYPE_CONFIG[t] : null;
              return (
                <motion.button key={t} whileHover={{ y: -1 }} whileTap={{ scale: 0.95 }}
                  onClick={() => setTypeFilter(t)}
                  style={{
                    fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 100,
                    cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)',
                    border: active ? 'none' : '1.5px solid var(--tandm-line)',
                    background: active ? (cfg ? cfg.bg : 'var(--tandm-ink)') : 'var(--tandm-surface)',
                    color: active ? (cfg ? cfg.color : '#fff') : 'var(--tandm-body)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  {t} {t !== 'All' && `(${counts[t]})`}
                </motion.button>
              );
            })}
          </div>

          {/* Page filter */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 8, paddingLeft: 8, borderLeft: '1px solid var(--tandm-line)' }}>
            {pages.map(p => (
              <motion.button key={p} whileHover={{ y: -1 }} whileTap={{ scale: 0.95 }}
                onClick={() => setPageFilter(p)}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 100,
                  cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)',
                  border: pageFilter === p ? 'none' : '1.5px solid var(--tandm-line)',
                  background: pageFilter === p ? 'var(--tandm-ink)' : 'var(--tandm-surface)',
                  color: pageFilter === p ? '#fff' : 'var(--tandm-body)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                {p}
              </motion.button>
            ))}
          </div>

          {/* Refresh */}
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={fetchFeedback}
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--tandm-body)', background: 'var(--tandm-surface)', border: '1.5px solid var(--tandm-line)', borderRadius: 100, padding: '6px 14px', cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)' }}
          >
            <Ic.refresh /> Refresh
          </motion.button>
        </div>
      </FadeIn>

      {/* Results count */}
      <p style={{ fontSize: 12, color: 'var(--tandm-muted)', margin: '0 0 14px' }}>
        {loading ? 'Loading...' : `${filtered.length} feedback item${filtered.length !== 1 ? 's' : ''}`}
      </p>

      {/* Feedback list */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <div style={{ width: 32, height: 32, border: '3px solid var(--tandm-canvas-deep)', borderTopColor: 'var(--tandm-accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 24px', background: 'var(--tandm-surface)', backdropFilter: 'blur(16px)', border: '1px solid var(--tandm-line)', borderRadius: 20 }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--tandm-ink)', marginBottom: 6 }}>No feedback yet</p>
          <p style={{ fontSize: 13, color: 'var(--tandm-muted)' }}>Feedback from your users will appear here</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <AnimatePresence>
            {filtered.map((item, i) => {
              const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.General;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ delay: i * 0.03 }}
                  whileHover={{ y: -2, boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }}
                  style={{
                    background: 'var(--tandm-surface)', backdropFilter: 'blur(16px)',
                    border: '1px solid var(--tandm-line)', borderRadius: 16,
                    padding: '16px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    {/* Avatar */}
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--tandm-gradient-cta)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                      {item.user_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Header row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-ink)' }}>{item.user_name || 'Anonymous'}</span>
                        <span style={{ fontSize: 11, color: 'var(--tandm-muted)' }}>{item.email}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, padding: '2px 9px', borderRadius: 100, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.dot }} />
                          {item.type}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 500, background: 'var(--tandm-canvas-deep)', color: 'var(--tandm-body)', padding: '2px 9px', borderRadius: 100 }}>
                          {item.page}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--tandm-faint)', display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                          <Ic.clock /> {formatDate(item.created_at)}
                        </span>
                      </div>

                      {/* Message */}
                      <p style={{ fontSize: 13, color: 'var(--tandm-ink-soft)', margin: 0, lineHeight: 1.6, background: 'var(--tandm-canvas)', borderRadius: 10, padding: '10px 12px', border: '1px solid var(--tandm-line)' }}>
                        {item.message}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

/* ─── Link Checker Tab ───────────────────────────────────── */
const STATUS_CFG = {
  alive:   { bg: '#ECFDF5', color: '#065F46', border: '#A7F3D0', dot: '#10B981', label: 'Alive' },
  dead:    { bg: '#FFF0F0', color: '#991B1B', border: '#FECACA', dot: '#EF4444', label: 'Dead' },
  blocked: { bg: '#FFF8E8', color: '#92400E', border: '#FDE68A', dot: '#F59E0B', label: 'Blocked' },
  error:   { bg: 'var(--tandm-canvas-deep)', color: 'var(--tandm-body)', border: 'var(--tandm-line)', dot: 'var(--tandm-muted)', label: 'Error' },
};

function LinkCheckerTab() {
  const [checking, setChecking]   = useState(false);
  const [results,  setResults]    = useState([]);
  const [total,    setTotal]      = useState(0);
  const [progress, setProgress]   = useState(0);
  const [deleting, setDeleting]   = useState({});
  const [deletingAll, setDeletingAll] = useState(false);
  const [filter,   setFilter]     = useState('all');
  const [startTime, setStartTime] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const abortRef = useRef(false);

  // Live-tick the elapsed timer while a check is running
  useEffect(() => {
    if (!checking || !startTime) return;
    const iv = setInterval(() => setElapsedMs(Date.now() - startTime), 250);
    return () => clearInterval(iv);
  }, [checking, startTime]);

  const startCheck = async () => {
    const t0 = Date.now();
    setStartTime(t0);
    setElapsedMs(0);
    setChecking(true);
    setResults([]);
    setProgress(0);
    abortRef.current = false;

    try {
      const res = await api.get('/internships');
      const jobs = (res.data.internships || []).filter(j => j.application_url);
      setTotal(jobs.length);

      // Check several links at once (pooled) instead of one-at-a-time — the backend
      // reuses a single browser, so parallel checks are dramatically faster.
      const CONCURRENCY = 6;
      let next = 0;
      let done = 0;
      const worker = async () => {
        while (!abortRef.current) {
          const i = next++;
          if (i >= jobs.length) break;
          const job = jobs[i];
          try {
            const r = await api.post('/admin/check-link', { id: job.id, url: job.application_url });
            setResults(prev => [...prev, { ...r.data, company: job.company_name, title: job.title }]);
          } catch {
            setResults(prev => [...prev, { id: job.id, company: job.company_name, title: job.title, url: job.application_url, status: 'error', reason: 'Request failed' }]);
          }
          done += 1;
          setProgress(done);
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, jobs.length) }, worker)
      );
    } catch {}
    finally {
      setElapsedMs(Date.now() - t0);
      setChecking(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(prev => ({ ...prev, [id]: true }));
    try {
      await api.delete(`/internships/${id}`);
      setResults(prev => prev.filter(r => r.id !== id));
    } catch {}
    setDeleting(prev => ({ ...prev, [id]: false }));
  };

  const handleDeleteAll = async () => {
    const deadIds = results.filter(r => r.status === 'dead').map(r => r.id);
    if (deadIds.length === 0) return;
    if (!window.confirm(`Delete all ${deadIds.length} dead role${deadIds.length === 1 ? '' : 's'}? This can't be undone.`)) return;
    setDeletingAll(true);
    const CONCURRENCY = 5;
    const deleted = new Set();
    const queue = [...deadIds];
    const worker = async () => {
      while (queue.length) {
        const id = queue.shift();
        try {
          await api.delete(`/internships/${id}`);
          deleted.add(id);
        } catch {}
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, deadIds.length) }, worker));
    setResults(prev => prev.filter(r => !deleted.has(r.id)));
    setDeletingAll(false);
  };

  const counts = {
    all:     results.length,
    alive:   results.filter(r => r.status === 'alive').length,
    dead:    results.filter(r => r.status === 'dead').length,
    blocked: results.filter(r => r.status === 'blocked' || r.status === 'error').length,
  };
  const visible = filter === 'all' ? results : results.filter(r =>
    filter === 'blocked' ? (r.status === 'blocked' || r.status === 'error') : r.status === filter
  );
  const pct = total > 0 ? Math.round((progress / total) * 100) : 0;
  const fmtDur = (ms) => {
    const s = Math.max(0, Math.round(ms / 1000));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      : `${m}:${String(sec).padStart(2, '0')}`;
  };
  // ETA from the running average per-listing rate — sharpens as more complete
  const remainingMs = checking && progress > 0
    ? (elapsedMs / progress) * (total - progress)
    : 0;

  return (
    <div>
      <FadeIn>
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--tandm-ink)', margin: '0 0 5px', letterSpacing: '-0.02em' }}>Link Checker</h2>
              <p style={{ fontSize: 13, color: 'var(--tandm-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
                Headless browser check — detects dead pages, closed roles, and JS-rendered content that curl misses.
              </p>

              {/* Progress */}
              {total > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tandm-body)' }}>
                      {checking ? `Checking ${Math.min(progress + 1, total)} of ${total}…` : `Done — ${progress} checked`}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--tandm-muted)' }}>{pct}%</span>
                  </div>
                  <div style={{ height: 5, background: 'var(--tandm-canvas-deep)', borderRadius: 100, overflow: 'hidden' }}>
                    <motion.div animate={{ width: `${pct}%` }} transition={{ ease: 'easeOut' }}
                      style={{ height: '100%', background: 'var(--tandm-gradient-cta)', borderRadius: 100 }} />
                  </div>
                  {/* Timer */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <span style={{ fontSize: 11, color: 'var(--tandm-muted)', fontVariantNumeric: 'tabular-nums' }}>
                      {checking ? 'Elapsed ' : 'Completed in '}{fmtDur(elapsedMs)}
                    </span>
                    {checking && progress > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tandm-accent)', fontVariantNumeric: 'tabular-nums' }}>
                        ~{fmtDur(remainingMs)} remaining
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Summary chips */}
              {results.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { key: 'all', label: `All (${counts.all})`, bg: 'var(--tandm-canvas-deep)', color: 'var(--tandm-body)', border: 'var(--tandm-line)' },
                    { key: 'alive',   label: `Alive (${counts.alive})`,   ...STATUS_CFG.alive },
                    { key: 'dead',    label: `Dead (${counts.dead})`,     ...STATUS_CFG.dead },
                    { key: 'blocked', label: `Blocked (${counts.blocked})`, ...STATUS_CFG.blocked },
                  ].map(f => (
                    <motion.button key={f.key} whileTap={{ scale: 0.95 }}
                      onClick={() => setFilter(f.key)}
                      style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 100, cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)', border: `1.5px solid ${filter === f.key ? f.border : 'var(--tandm-line)'}`, background: filter === f.key ? f.bg : 'transparent', color: filter === f.key ? f.color : 'var(--tandm-muted)', transition: 'all 0.15s' }}
                    >
                      {f.label}
                    </motion.button>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-start', flexShrink: 0 }}>
              {!checking && counts.dead > 0 && (
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  onClick={handleDeleteAll} disabled={deletingAll}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#FFF0F0', color: '#991B1B', border: '1.5px solid #FECACA', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: deletingAll ? 'not-allowed' : 'pointer', fontFamily: 'var(--tandm-font-sans)', opacity: deletingAll ? 0.6 : 1 }}>
                  <Ic.trash /> {deletingAll ? 'Deleting…' : `Delete all dead (${counts.dead})`}
                </motion.button>
              )}
              {checking && (
                <motion.button whileTap={{ scale: 0.97 }} onClick={() => { abortRef.current = true; }}
                  style={{ padding: '10px 16px', borderRadius: 10, border: '1.5px solid var(--tandm-line)', background: 'var(--tandm-canvas-deep)', color: 'var(--tandm-body)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)' }}>
                  Stop
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 6px 20px rgba(78,108,126,0.3)' }} whileTap={{ scale: 0.97 }}
                onClick={startCheck} disabled={checking}
                style={{ display: 'flex', alignItems: 'center', gap: 7, background: checking ? 'var(--tandm-accent-light)' : 'var(--tandm-gradient-cta)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: checking ? 'not-allowed' : 'pointer', fontFamily: 'var(--tandm-font-sans)' }}
              >
                {checking
                  ? <><div style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Checking…</>
                  : <><Ic.link /> Check All Links</>}
              </motion.button>
            </div>
          </div>
        </Card>
      </FadeIn>

      {/* Results list */}
      {visible.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <AnimatePresence>
            {visible.map((r) => {
              const cfg = STATUS_CFG[r.status] || STATUS_CFG.error;
              return (
                <motion.div key={r.id}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.18 }}
                  style={{
                    background: 'var(--tandm-surface)', backdropFilter: 'blur(16px)',
                    border: `1.5px solid ${r.status === 'dead' ? '#FED7D7' : 'var(--tandm-line)'}`,
                    borderRadius: 13, padding: '12px 15px',
                    display: 'flex', alignItems: 'center', gap: 12,
                    boxShadow: r.status === 'dead' ? '0 2px 10px rgba(239,68,68,0.07)' : '0 1px 4px rgba(0,0,0,0.04)',
                  }}>
                  {/* Status badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 100, padding: '3px 10px', flexShrink: 0, minWidth: 68, justifyContent: 'center' }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color }}>{cfg.label}</span>
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, overflow: 'hidden' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-ink)', whiteSpace: 'nowrap' }}>{r.company}</span>
                      <span style={{ fontSize: 12, color: 'var(--tandm-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{r.title}</span>
                    </div>
                    {r.reason && (
                      <p style={{ fontSize: 11, color: 'var(--tandm-muted)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</p>
                    )}
                  </div>

                  <span style={{ fontSize: 11, color: 'var(--tandm-faint)', flexShrink: 0 }}>#{r.id}</span>

                  {r.status === 'dead' && (
                    <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                      onClick={() => handleDelete(r.id)} disabled={!!deleting[r.id]}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#FFF0F0', color: '#991B1B', border: '1px solid #FECACA', borderRadius: 8, padding: '5px 11px', fontSize: 12, fontWeight: 600, cursor: deleting[r.id] ? 'not-allowed' : 'pointer', fontFamily: 'var(--tandm-font-sans)', flexShrink: 0, opacity: deleting[r.id] ? 0.6 : 1 }}>
                      <Ic.trash /> {deleting[r.id] ? 'Deleting…' : 'Delete'}
                    </motion.button>
                  )}

                  {r.url && (
                    <a href={r.url} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--tandm-faint)', flexShrink: 0, display: 'flex', alignItems: 'center' }} title={r.url}>
                      <Ic.external />
                    </a>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      ) : !checking ? (
        <div style={{ textAlign: 'center', padding: '60px 24px', background: 'var(--tandm-surface)', backdropFilter: 'blur(16px)', border: '1px solid var(--tandm-line)', borderRadius: 20 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
          <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--tandm-ink)', margin: '0 0 6px' }}>No results yet</p>
          <p style={{ fontSize: 13, color: 'var(--tandm-muted)', margin: 0 }}>Click "Check All Links" — each listing will be verified with a headless browser.</p>
        </div>
      ) : null}
    </div>
  );
}

/* ─── AI Notes Tab ───────────────────────────────────────── */
function fmt(sec) {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function AINotesTab() {
  const [status, setStatus]       = useState(null);
  const [launching, setLaunching] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const r = await api.get('/ai/notes-status');
      setStatus(r.data);
      return r.data;
    } catch { return null; }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await fetchStatus();
      if (s && !s.running && s.remaining === 0) {
        clearInterval(pollRef.current);
      }
    }, 2000);
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  const handleGenerate = async () => {
    setLaunching(true);
    try {
      await api.post('/ai/internship-notes-batch');
      await fetchStatus();
      startPolling();
    } catch {}
    setLaunching(false);
  };

  const pct = status ? Math.round((status.done / status.total) * 100) : 0;
  const isRunning = status?.running;
  const allDone   = status && status.remaining === 0;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

      {/* ── Header card ── */}
      <FadeIn>
        <Card>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
            <div style={{ flex:1 }}>
              <h2 style={{ fontSize:16, fontWeight:700, color:'var(--tandm-ink)', margin:'0 0 4px', letterSpacing:'-0.02em' }}>AI Notes Generator</h2>
              <p style={{ fontSize:13, color:'var(--tandm-muted)', margin:'0 0 16px' }}>
                Generates skill requirements, tips, and key info for each internship using Llama 3.3 70B.
              </p>

              {/* Big stats row */}
              {status && (
                <div style={{ display:'flex', gap:20, marginBottom:16, flexWrap:'wrap' }}>
                  {[
                    { label:'Generated', value: status.done,      color:'#10B981' },
                    { label:'Remaining', value: status.remaining, color: status.remaining > 0 ? '#F59E0B' : '#10B981' },
                    { label:'Failed',    value: status.failed || 0, color:'#EF4444' },
                    { label:'Total',     value: status.total,     color:'#6366F1' },
                  ].map(s => (
                    <div key={s.label}>
                      <div style={{ fontSize:24, fontWeight:800, color:s.color, letterSpacing:'-0.04em', lineHeight:1 }}>{s.value}</div>
                      <div style={{ fontSize:10, fontWeight:600, color:'var(--tandm-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginTop:3 }}>{s.label}</div>
                    </div>
                  ))}
                  {isRunning && status.etaSec != null && (
                    <div>
                      <div style={{ fontSize:24, fontWeight:800, color:'var(--tandm-accent)', letterSpacing:'-0.04em', lineHeight:1 }}>{fmt(status.etaSec)}</div>
                      <div style={{ fontSize:10, fontWeight:600, color:'var(--tandm-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginTop:3 }}>ETA</div>
                    </div>
                  )}
                </div>
              )}

              {/* Progress bar */}
              {status && (
                <div style={{ marginBottom:0 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                    <span style={{ fontSize:12, fontWeight:600, color:'var(--tandm-body)' }}>
                      {status?.retrying ? 'Retrying failed...' : isRunning ? 'Generating...' : allDone ? 'Complete ✓' : 'Paused'}
                    </span>
                    <span style={{ fontSize:12, fontWeight:700, color:'var(--tandm-accent)' }}>{pct}%</span>
                  </div>
                  <div style={{ height:8, background:'var(--tandm-line)', borderRadius:100, overflow:'hidden' }}>
                    <motion.div
                      animate={{ width:`${pct}%` }}
                      transition={{ ease:'easeOut', duration:0.5 }}
                      style={{ height:'100%', background: allDone ? '#10B981' : 'var(--tandm-gradient-cta)', borderRadius:100 }}
                    />
                  </div>
                </div>
              )}
            </div>

            <motion.button
              whileHover={!isRunning && !launching ? { scale:1.03, boxShadow:'0 6px 20px rgba(78,108,126,0.3)' } : {}}
              whileTap={!isRunning && !launching ? { scale:0.97 } : {}}
              onClick={handleGenerate}
              disabled={isRunning || launching}
              style={{
                display:'flex', alignItems:'center', gap:8, flexShrink:0, alignSelf:'flex-start',
                background: isRunning || launching ? 'var(--tandm-accent-light)' : 'var(--tandm-gradient-cta)',
                color:'#fff', border:'none', borderRadius:10, padding:'11px 20px',
                fontSize:13, fontWeight:600, fontFamily:'var(--tandm-font-sans)',
                cursor: isRunning || launching ? 'not-allowed' : 'pointer',
              }}
            >
              {isRunning || launching
                ? <><div style={{ width:13, height:13, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} /> {isRunning ? 'Running…' : 'Starting…'}</>
                : <><Ic.sparkle /> {allDone ? 'Regenerate All' : 'Generate Missing'}</>}
            </motion.button>
          </div>
        </Card>
      </FadeIn>

      {/* ── Currently processing ── */}
      <AnimatePresence>
        {isRunning && status.currentlyProcessing?.length > 0 && (
          <motion.div key="processing" initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--tandm-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>
              Currently generating
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {status.currentlyProcessing.map(item => (
                <div key={item.id} style={{ display:'flex', alignItems:'center', gap:10, background:'var(--tandm-surface)', backdropFilter:'blur(16px)', border:'1.5px solid #FDE68A', borderRadius:12, padding:'10px 14px', boxShadow:'0 2px 8px rgba(78,108,126,0.07)' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:'#F59E0B', animation:'pulse 1.2s ease infinite', flexShrink:0 }} />
                  <span style={{ fontSize:13, fontWeight:600, color:'var(--tandm-ink)', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.title}</span>
                  <span style={{ fontSize:12, color:'var(--tandm-muted)', flexShrink:0 }}>{item.company}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Recently completed feed ── */}
      <AnimatePresence>
        {status?.recentlyCompleted?.length > 0 && (
          <motion.div key="completed" initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--tandm-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>
              Recently completed
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              <AnimatePresence initial={false}>
                {status.recentlyCompleted.map((item, i) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity:0, x:-16, height:0 }}
                    animate={{ opacity:1, x:0, height:'auto' }}
                    exit={{ opacity:0, height:0 }}
                    transition={{ duration:0.25, delay: i === 0 ? 0 : 0 }}
                    style={{ overflow:'hidden' }}
                  >
                    <div style={{ display:'flex', alignItems:'center', gap:10, background:'var(--tandm-surface)', backdropFilter:'blur(12px)', border:`1px solid ${item.failed ? '#FECACA' : '#A7F3D0'}`, borderRadius:10, padding:'8px 13px', marginBottom:0 }}>
                      <div style={{ width:7, height:7, borderRadius:'50%', background: item.failed ? '#EF4444' : '#10B981', flexShrink:0 }} />
                      <span style={{ fontSize:12, fontWeight:600, color:'var(--tandm-ink)', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.title}</span>
                      <span style={{ fontSize:11, color:'var(--tandm-muted)', flexShrink:0 }}>{item.company}</span>
                      {item.failed && <span style={{ fontSize:10, color:'#EF4444', fontWeight:600, flexShrink:0 }}>FAILED</span>}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Greenhouse Coverage Tab ────────────────────────────── */
const GH_STATUS_CFG = {
  full_automation:      { bg: '#ECFDF5', color: '#065F46', border: '#A7F3D0', label: 'Full automation'     },
  assisted_automation:  { bg: '#FFFBEB', color: '#92400E', border: '#FDE68A', label: 'Assisted automation' },
  blocked_partial:      { bg: '#FEF2F2', color: '#991B1B', border: '#FCA5A5', label: 'Blocked / partial'    },
  full_autofill_ready: { bg: '#ECFDF5', color: '#065F46', border: '#A7F3D0', label: 'Full autofill ready' },
  ready_with_review:   { bg: '#FFFBEB', color: '#92400E', border: '#FDE68A', label: 'Ready with review'   },
  partial_autofill:    { bg: '#FFF7ED', color: '#9A5809', border: '#F5D9B6', label: 'Partial autofill'    },
  blocked:             { bg: '#FEF2F2', color: '#991B1B', border: '#FCA5A5', label: 'Blocked'             },
};

function GreenhouseCoverageTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg]   = useState('');

  const fetchDiagnostics = async () => {
    setLoading(true);
    try {
      const res = await api.get('/apply/greenhouse/diagnostics');
      setData(res.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDiagnostics(); }, []);

  const handleScanAll = async () => {
    setScanning(true);
    setScanMsg('');
    try {
      const res = await api.post('/apply/greenhouse/scan-all', { limit: 500 });
      const updated = Array.isArray(res.data.scanned) ? res.data.scanned.length : (res.data.total || '?');
      setScanMsg(`Scan complete — ${updated} checked`);
      await fetchDiagnostics();
    } catch (err) {
      setScanMsg(err.response?.data?.error || 'Scan failed');
    } finally { setScanning(false); }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div style={{ width: 32, height: 32, border: '3px solid var(--tandm-canvas-deep)', borderTopColor: 'var(--tandm-accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      </div>
    );
  }

  if (!data) {
    return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--tandm-muted)', fontSize: 14 }}>Could not load diagnostics.</div>;
  }

  const statusOrder = ['full_automation', 'assisted_automation', 'blocked_partial'];
  const supportCounts = data.by_support_tier || data.by_ready_status || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* ── Header + scan button ── */}
      <FadeIn>
        <Card>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--tandm-ink)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Greenhouse Coverage</h2>
              <p style={{ fontSize: 13, color: 'var(--tandm-muted)', margin: 0 }}>
                {data.totals.schemas_scanned} schemas scanned · {data.totals.autofill_sessions} autofill sessions · avg {data.totals.avg_coverage_score}% coverage
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <motion.button
                whileHover={!scanning ? { scale: 1.02, boxShadow: '0 6px 20px rgba(78,108,126,0.3)' } : {}}
                whileTap={!scanning ? { scale: 0.97 } : {}}
                onClick={handleScanAll}
                disabled={scanning}
                style={{ display: 'flex', alignItems: 'center', gap: 7, background: scanning ? 'var(--tandm-accent-light)' : 'var(--tandm-gradient-cta)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: scanning ? 'not-allowed' : 'pointer', fontFamily: 'var(--tandm-font-sans)' }}
              >
                {scanning
                  ? <><div style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Scanning…</>
                  : <><Ic.refresh /> Scan all listings</>}
              </motion.button>
              {scanMsg && <p style={{ fontSize: 12, color: 'var(--tandm-body)', margin: 0 }}>{scanMsg}</p>}
            </div>
          </div>
        </Card>
      </FadeIn>

      {/* ── Readiness distribution ── */}
      <FadeIn delay={0.05}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10 }}>
          {statusOrder.map((status) => {
            const cfg = GH_STATUS_CFG[status] || GH_STATUS_CFG.blocked;
            const count = supportCounts[status] || 0;
            const pct = data.totals.schemas_scanned > 0
              ? Math.round((count / data.totals.schemas_scanned) * 100)
              : 0;
            return (
              <div key={status} style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 16, padding: '16px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{cfg.label}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: cfg.color, letterSpacing: '-0.04em', lineHeight: 1 }}>{count}</div>
                <div style={{ fontSize: 11, color: cfg.color, opacity: 0.7, marginTop: 4 }}>{pct}% of scanned</div>
              </div>
            );
          })}
        </div>
      </FadeIn>

      {/* ── Coverage distribution + autofill stats ── */}
      <FadeIn delay={0.08}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Card>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-ink)', margin: '0 0 14px' }}>Coverage score distribution</h3>
            {Object.entries(data.coverage_distribution).map(([range, count]) => {
              const pct = data.totals.schemas_scanned > 0 ? Math.round((count / data.totals.schemas_scanned) * 100) : 0;
              const barColor = range === '90-100' ? '#10B981' : range === '70-89' ? '#F59E0B' : range === '50-69' ? '#F97316' : '#EF4444';
              return (
                <div key={range} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: 'var(--tandm-body)', fontWeight: 600 }}>{range}%</span>
                    <span style={{ fontSize: 12, color: 'var(--tandm-muted)' }}>{count} ({pct}%)</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--tandm-canvas-deep)', borderRadius: 100, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 100 }} />
                  </div>
                </div>
              );
            })}
          </Card>

          <Card>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-ink)', margin: '0 0 14px' }}>Autofill session stats</h3>
            {[
              { label: 'Total sessions',    value: data.autofill_stats.total_sessions },
              { label: 'Avg fields filled', value: data.autofill_stats.avg_fields_filled },
              { label: 'Resume attached',   value: `${data.autofill_stats.resume_attached_pct}%` },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--tandm-canvas-deep)' }}>
                <span style={{ fontSize: 13, color: 'var(--tandm-body)' }}>{label}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--tandm-ink)' }}>{value}</span>
              </div>
            ))}
          </Card>
        </div>
      </FadeIn>

      {/* ── Top risk flags ── */}
      {data.top_risk_flags.length > 0 && (
        <FadeIn delay={0.1}>
          <Card>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-ink)', margin: '0 0 14px' }}>Most common risk flags</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.top_risk_flags.map(({ code, count }) => (
                <div key={code} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#FFF7ED', border: '1px solid #F5D9B6', borderRadius: 10 }}>
                  <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#9A5809' }}>{code}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#9A5809' }}>{count}</span>
                </div>
              ))}
            </div>
          </Card>
        </FadeIn>
      )}

      {/* ── Worst fill rates ── */}
      {data.worst_fill_rates.length > 0 && (
        <FadeIn delay={0.12}>
          <Card>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-ink)', margin: '0 0 14px' }}>Lowest fill rates (from real sessions)</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.worst_fill_rates.map(({ normalized_key, attempts, fill_rate }) => (
                <div key={normalized_key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10 }}>
                  <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#991B1B' }}>{normalized_key}</span>
                  <div style={{ display: 'flex', gap: 10, fontSize: 12, color: '#991B1B' }}>
                    <span>{fill_rate}% fill</span>
                    <span style={{ color: 'var(--tandm-muted)' }}>{attempts} attempts</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </FadeIn>
      )}

      {/* ── Schemas needing attention ── */}
      {data.attention_needed.length > 0 && (
        <FadeIn delay={0.14}>
          <Card>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-ink)', margin: '0 0 14px' }}>Schemas needing attention</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.attention_needed.map((s, i) => {
                const cfg = GH_STATUS_CFG[s.support_tier || s.ready_status] || GH_STATUS_CFG.blocked_partial;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--tandm-surface)', border: `1px solid ${cfg.border}`, borderRadius: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.company}</div>
                      <div style={{ fontSize: 11, color: 'var(--tandm-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, borderRadius: 100, padding: '2px 9px' }}>{cfg.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--tandm-muted)', background: 'var(--tandm-canvas-deep)', borderRadius: 100, padding: '2px 9px' }}>{s.coverage_score}%</span>
                      {s.risk_count > 0 && <span style={{ fontSize: 11, color: '#9A5809', background: '#FFF7ED', border: '1px solid #F5D9B6', borderRadius: 100, padding: '2px 9px' }}>{s.risk_count} risk{s.risk_count !== 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </FadeIn>
      )}
    </div>
  );
}

/* ─── Main Admin ─────────────────────────────────────────── */
export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('internships');
  const [stats, setStats] = useState({ internships: 0, users: 0, applications: 0 });

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/dashboard');
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      const [intRes, appRes] = await Promise.all([
        api.get('/internships'),
        api.get('/applications/my'),
      ]);
      setStats({
        internships: intRes.data.internships?.length || 0,
        applications: appRes.data.applications?.length || 0,
      });
    } catch {}
  };

  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');

  const handleImport = async () => {
    setImporting(true);
    setImportStatus('Importing...');
    try {
      const res = await api.post('/admin/import-internships');
      setImportStatus(`Done — ${res.data.imported} imported, ${res.data.errors} errors`);
      fetchStats();
    } catch (err) {
      setImportStatus(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const TABS = [
    { id: 'internships',   label: 'Internships',  icon: <Ic.internship /> },
    { id: 'ai',            label: 'AI Notes',     icon: <Ic.sparkle />    },
    { id: 'greenhouse',    label: 'Coverage',     icon: <Ic.zap />        },
    { id: 'link-checker',  label: 'Link Checker', icon: <Ic.link />       },
    { id: 'users',         label: 'Users',        icon: <Ic.users />      },
    { id: 'waitlist',      label: 'Waitlist',     icon: <Ic.message />    },
    { id: 'feedback',      label: 'Feedback',     icon: <Ic.zap />        },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--tandm-canvas)', fontFamily: 'var(--tandm-font-sans)' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.3)} }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-thumb { background: var(--tandm-line-strong); border-radius: 3px; }
        .admin-tabs::-webkit-scrollbar { height: 0; }
      `}</style>

      <OfferDeskNav />

      <div style={{ maxWidth: 940, margin: '0 auto', padding: 'var(--tandm-nav-clearance) 24px 72px' }}>

        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{ marginBottom: 28 }}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--tandm-accent-subtle)', border: '1px solid var(--tandm-line)', borderRadius: 100, padding: '5px 14px', marginBottom: 14 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--tandm-accent-hot)', animation: 'pulse 2s ease infinite' }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tandm-accent-deep)', letterSpacing: '0.05em' }}>Admin panel</span>
          </div>
          <h1 style={{ fontFamily: 'var(--tandm-font-display)', fontSize: 'clamp(28px,3.4vw,42px)', fontWeight: 500, color: 'var(--tandm-ink)', letterSpacing: '-0.03em', lineHeight: 1.08, margin: '0 0 18px' }}>
            Tandm Admin
          </h1>

          {/* Inline stats */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {[
              { icon: <Ic.internship />, n: stats.internships, l: 'Internships' },
              { icon: <Ic.message />,    n: stats.applications, l: 'Applications' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, ...(i > 0 ? { paddingLeft: 24, borderLeft: '1px solid var(--tandm-line)' } : {}) }}>
                <div style={{ color: 'var(--tandm-accent)' }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--tandm-ink)', letterSpacing: '-0.03em', lineHeight: 1 }}>{s.n}</div>
                  <div style={{ fontSize: 10, color: 'var(--tandm-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{s.l}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Tabs */}
        <FadeIn>
          <div className="admin-tabs" style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--tandm-surface)', borderRadius: 14, padding: 5, width: 'fit-content', maxWidth: '100%', overflowX: 'auto', border: '1px solid var(--tandm-line)', boxShadow: 'var(--tandm-shadow-soft)' }}>
            {TABS.map(t => (
              <motion.button
                key={t.id}
                whileTap={{ scale: 0.97 }}
                onClick={() => setTab(t.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '9px 16px', borderRadius: 10, cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, fontFamily: 'var(--tandm-font-sans)',
                  border: 'none', whiteSpace: 'nowrap',
                  background: tab === t.id ? 'var(--tandm-accent-subtle)' : 'transparent',
                  color: tab === t.id ? 'var(--tandm-accent-deep)' : 'var(--tandm-body)',
                  transition: 'all 0.2s',
                }}
              >
                <span style={{ color: tab === t.id ? 'var(--tandm-accent)' : 'var(--tandm-faint)' }}>{t.icon}</span>
                {t.label}
              </motion.button>
            ))}
          </div>
        </FadeIn>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          {tab === 'internships' && (
            <motion.div key="internships" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
              <FadeIn>
                <Card>
                  <h2 style={{ fontFamily:'var(--tandm-font-display)', fontSize:20, fontWeight:500, color:'var(--tandm-ink)', margin:'0 0 6px', letterSpacing:'-0.02em' }}>Internship database</h2>
                  <p style={{ fontSize:13, color:'var(--tandm-muted)', margin:'0 0 20px' }}>{stats.internships} internships currently live</p>
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                    <motion.button
                      whileHover={{ scale:1.02, boxShadow:'0 6px 20px rgba(78,108,126,0.3)' }}
                      whileTap={{ scale:0.97 }}
                      onClick={() => navigate('/jobs')}
                      style={{ display:'flex', alignItems:'center', gap:7, background:'var(--tandm-gradient-cta)', color:'#fff', border:'none', borderRadius:10, padding:'10px 18px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'var(--tandm-font-sans)' }}
                    >
                      <Ic.internship /> View all internships
                    </motion.button>
                    <motion.button
                      whileHover={{ scale:1.02 }}
                      whileTap={{ scale:0.97 }}
                      onClick={handleImport}
                      disabled={importing}
                      style={{ display:'flex', alignItems:'center', gap:7, background:'var(--tandm-surface)', color:'var(--tandm-ink)', border:'1.5px solid var(--tandm-line)', borderRadius:10, padding:'10px 18px', fontSize:13, fontWeight:600, cursor:importing?'not-allowed':'pointer', fontFamily:'var(--tandm-font-sans)', opacity: importing ? 0.6 : 1 }}
                    >
                      {importing
                        ? <><div style={{ width:13, height:13, border:'2px solid var(--tandm-line)', borderTopColor:'var(--tandm-accent)', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} /> Importing...</>
                        : <><Ic.refresh /> Import from CSV</>}
                    </motion.button>
                  </div>
                  {importStatus && (
                    <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }} style={{ fontSize:13, color:'var(--tandm-body)', margin:'14px 0 0', background:'var(--tandm-canvas-deep)', borderRadius:10, padding:'10px 14px' }}>
                      {importStatus}
                    </motion.p>
                  )}
                </Card>
              </FadeIn>
            </motion.div>
          )}

          {tab === 'ai' && (
            <motion.div key="ai" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
              <AINotesTab />
            </motion.div>
          )}

          {tab === 'greenhouse' && (
            <motion.div key="greenhouse" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
              <GreenhouseCoverageTab />
            </motion.div>
          )}

          {tab === 'link-checker' && (
            <motion.div key="link-checker" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
              <LinkCheckerTab />
            </motion.div>
          )}

          {tab === 'users' && (
            <motion.div key="users" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
              <UsersTab />
            </motion.div>
          )}

          {tab === 'waitlist' && (
            <motion.div key="waitlist" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
              <WaitlistTab />
            </motion.div>
          )}

          {tab === 'feedback' && (
            <motion.div key="feedback" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
              <FeedbackTab />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
